import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Building2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { siteConfig } from "../mock";

export default function Contact() {
  const { t } = useLanguage();
  const { ref, visible } = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const f = t.contact.form;

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <section id="contatti" className="py-20 lg:py-28 hsc-net-bg">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14 hsc-reveal ${
          visible ? "is-visible" : ""
        }`}
      >
        {/* Info side */}
        <div className="lg:col-span-2">
          <span className="text-primary font-semibold text-sm tracking-[0.18em] uppercase">
            {t.contact.eyebrow}
          </span>
          <h2 className="mt-3 font-display font-extrabold text-slate-900 text-[28px] sm:text-[34px] leading-tight tracking-[-0.02em]">
            {t.contact.title}
          </h2>
          <p className="mt-4 text-[15.5px] leading-relaxed text-slate-700">{t.contact.subtitle}</p>

          <div className="mt-8 space-y-5">
            <a href={siteConfig.phoneHref} className="flex items-center gap-3.5 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
              </span>
              <span className="text-[15px] font-semibold text-slate-800">{t.contact.phone}</span>
            </a>
            <a href={`mailto:${siteConfig.email}`} className="flex items-center gap-3.5 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
              </span>
              <span className="text-[15px] font-semibold text-slate-800">{t.contact.email}</span>
            </a>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-secondary/70 border border-slate-100 p-5">
              <div className="flex items-center gap-2 text-primary">
                <Building2 className="w-4 h-4" />
                <span className="text-[12px] font-bold uppercase tracking-wider">{t.contact.mgmtTitle}</span>
              </div>
              <div className="mt-3 space-y-0.5 text-[13.5px] text-slate-700">
                {t.contact.mgmt.map((l, i) => <p key={i}>{l}</p>)}
              </div>
            </div>
            <div className="rounded-2xl bg-secondary/70 border border-slate-100 p-5">
              <div className="flex items-center gap-2 text-primary">
                <MapPin className="w-4 h-4" />
                <span className="text-[12px] font-bold uppercase tracking-wider">{t.contact.hqTitle}</span>
              </div>
              <div className="mt-3 space-y-0.5 text-[13.5px] text-slate-700">
                {t.contact.hq.map((l, i) => <p key={i}>{l}</p>)}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex items-start gap-2 text-[13px] text-slate-700">
                <Clock className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-700">{t.contact.hoursTitle}</p>
                  {t.contact.hours.map((h, i) => <p key={i}>{h}</p>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form side */}
        <div className="lg:col-span-3">
          <form
            action="https://formsubmit.co/antonio.de@libero.it"
            method="POST"
            className="bg-secondary/40 rounded-3xl border border-slate-100 p-6 sm:p-8 hsc-card-shadow"
          >
            {/* FormSubmit configuration */}
            <input type="hidden" name="_next" value="https://netlify.app" />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_subject" value="Hotspot Costarei – Richiesta dal sito" />
            <input type="text" name="_honey" style={{ display: "none" }} />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{f.name}</label>
                <Input name="name" value={form.name} onChange={update("name")} required className="h-12 bg-white" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{f.email}</label>
                <Input type="email" name="email" value={form.email} onChange={update("email")} required className="h-12 bg-white" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{f.phone}</label>
                <Input name="phone" value={form.phone} onChange={update("phone")} className="h-12 bg-white" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{f.subject}</label>
                <Select value={form.subject} onValueChange={(v) => setForm((p) => ({ ...p, subject: v }))}>
                  <SelectTrigger className="h-12 bg-white">
                    <SelectValue placeholder={f.subject} />
                  </SelectTrigger>
                  <SelectContent>
                    {f.subjectOptions.map((opt, i) => (
                      <SelectItem key={i} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Carry the Select value for submission */}
                <input type="hidden" name="subject" value={form.subject} />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{f.message}</label>
              <Textarea name="message" value={form.message} onChange={update("message")} required rows={5} className="bg-white resize-none" />
            </div>
            <Button type="submit" className="mt-5 w-full sm:w-auto rounded-full h-12 px-8 font-semibold">
              {f.send}<Send className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
